/**
 * Regras puras da tela de entrada (login + verificação em duas etapas).
 *
 * Ficam fora dos componentes para serem testadas sem montar a tela e para
 * que o componente só decida o que mostrar.
 */

/** Período do TOTP do Google Authenticator (RFC 6238). */
export const PERIODO_TOTP_SEGUNDOS = 30;

/** Abaixo disto o anel do código fica âmbar ("o código está para mudar"). */
export const SEGUNDOS_TOTP_ACABANDO = 5;

/** Circunferência do anel do design (r = 9 → 2πr ≈ 56,55). */
export const CIRCUNFERENCIA_DO_ANEL = 56.55;

export const DIGITOS_TOTP = 6;

/** E-mail que o design mostra quando não sabemos qual a pessoa digitou. */
export const EMAIL_GENERICO = "nome@suamarcenaria.com.br";

const FORMATO_EMAIL = /^\S+@\S+\.\S+$/;
const FORMATO_RECUPERACAO = /^[A-Z0-9]{4}-?[A-Z0-9]{4}$/;

export function emailValido(email) {
  return FORMATO_EMAIL.test(String(email || "").trim());
}

/**
 * "igor@exemplo.com.br" → "ig•••@exemplo.com.br".
 * Sem e-mail utilizável, devolve o texto genérico do design.
 */
export function mascararEmail(email) {
  const limpo = String(email || "").trim();
  const arroba = limpo.indexOf("@");
  if (arroba < 1) return EMAIL_GENERICO;
  return `${limpo.slice(0, Math.min(2, arroba))}•••${limpo.slice(arroba)}`;
}

/** Segundos até o app autenticador trocar de código, alinhado ao relógio. */
export function segundosAteProximoCodigo(agoraMs) {
  return PERIODO_TOTP_SEGUNDOS - (Math.floor(agoraMs / 1000) % PERIODO_TOTP_SEGUNDOS);
}

/** Quanto do anel já "esvaziou" (stroke-dashoffset do círculo). */
export function deslocamentoDoAnel(segundosRestantes) {
  return Number((CIRCUNFERENCIA_DO_ANEL * (1 - segundosRestantes / PERIODO_TOTP_SEGUNDOS)).toFixed(2));
}

export function codigoAcabando(segundosRestantes) {
  return segundosRestantes <= SEGUNDOS_TOTP_ACABANDO;
}

/** Só dígitos, no máximo 6 — aceita colar "123 456" ou "123-456". */
export function normalizarCodigoTotp(valor) {
  return String(valor || "").replace(/\D/g, "").slice(0, DIGITOS_TOTP);
}

/** O que a pessoa digita no campo de recuperação: maiúsculas, A-Z/0-9/hífen. */
export function normalizarDigitacaoRecuperacao(valor) {
  return String(valor || "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, "")
    .slice(0, 9);
}

export function codigoRecuperacaoValido(valor) {
  return FORMATO_RECUPERACAO.test(String(valor || ""));
}

/**
 * Formato que o backend compara: XXXX-YYYY. O hash foi gerado COM o hífen,
 * então quem digita "ABCD1234" precisa ter o hífen recolocado.
 */
export function codigoRecuperacaoParaEnvio(valor) {
  const limpo = String(valor || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return `${limpo.slice(0, 4)}-${limpo.slice(4, 8)}`;
}

/** "m:ss" para a validade da verificação (o mfa_token dura 5 minutos). */
export function formatarMinutosSegundos(segundos) {
  if (segundos <= 0) return "0:00";
  const minutos = Math.floor(segundos / 60);
  return `${minutos}:${String(segundos % 60).padStart(2, "0")}`;
}

export const MENSAGEM_CODIGO_INVALIDO = "Código inválido ou expirado. Use o código atual do app.";

/**
 * Texto do erro da verificação. Prefere o que o backend disse ("Bloqueado
 * por muitas falhas. Tente novamente em 30 minutos.", "Código expirado ou
 * já utilizado."); só cai no texto do design quando a resposta não trouxe
 * mensagem (o "Falha na API (500)" genérico do buildError).
 */
export function mensagemDeFalhaDaVerificacao(erro) {
  const mensagem = String(erro?.message || "").trim();
  if (!mensagem || /^Falha na API/i.test(mensagem)) return MENSAGEM_CODIGO_INVALIDO;
  return mensagem;
}

/** Caps Lock ligado segundo o evento de teclado (nem todo evento sabe dizer). */
export function capsLockLigado(evento) {
  return Boolean(evento && typeof evento.getModifierState === "function" && evento.getModifierState("CapsLock"));
}
